<template>
  <v-table
    class="v-table-header-line-full"
    :count="count"
    :list="list"
    :is-loading="isLoading"
    :push-list="RouteConfig.administratorOperationLogList.push"
    :query-list="queryList"
    :is-show-administrator-select="true"
    administrator-select-label="操作人"
    :is-show-update-administrator-select="true"
    update-administrator-select-label="操作对象"
    :is-show-search-input="true"
    search-placeholder="输入 IP 或动作标识"
  >
    <div slot="header-item">
      <el-select
        v-model="queryList.target_type"
        clearable
        size="small"
        class="table-filter-select"
        placeholder="对象类型"
        @change="handleFilterChange"
      >
        <el-option v-for="item in targetTypeOptions" :key="item.value" :label="item.name" :value="item.value"/>
      </el-select>
      <!-- 动作有 22 项，平铺在一个人很少翻的下拉里等于不可用；按业务环节分组（el-option-group），
           组名来自 @/enums/operationActions 的 group 字段，保留 filterable 以支持直接打关键字过滤 -->
      <el-select
        v-model="queryList.action"
        clearable
        filterable
        size="small"
        class="table-filter-select"
        placeholder="操作动作"
        @change="handleFilterChange"
      >
        <el-option-group
          v-for="group in actionGroups"
          :key="group.name"
          :label="group.name"
        >
          <el-option
            v-for="item in group.options"
            :key="item.value"
            :label="item.name"
            :value="item.value"
          />
        </el-option-group>
      </el-select>
    </div>

    <el-table-column label="ID" prop="id" width="70"/>
    <el-table-column label="操作人" width="150">
      <template v-slot="{ row }">
        <v-avatar-nickname
          :value="row.operator_avatar"
          :nickname="row.operator_nickname || (`#${row.operator_id}`)"
          :avatar-size="28"
          :nickname-overflow="true"
          width="140px"
        />
      </template>
    </el-table-column>
    <el-table-column label="操作对象" width="270">
      <template v-slot="{ row }">
        <div class="target-cell">
          <div class="target-line">
            <el-tag :type="targetTypeTag(row)" size="mini" class="target-tag">{{ row.target_type_name }}</el-tag>
            <router-link
              v-if="targetLink(row)"
              :to="targetLink(row)"
              class="target-name target-name--link"
              :title="targetDisplayName(row)"
            >{{ targetDisplayName(row) }}</router-link>
            <span v-else class="target-name" :title="targetDisplayName(row)">{{ targetDisplayName(row) }}</span>
            <span v-if="!row.target_exists" class="target-gone">已失效</span>
          </div>
          <div v-if="row.target_desc" class="target-desc" :title="row.target_desc">{{ row.target_desc }}</div>
        </div>
      </template>
    </el-table-column>
    <el-table-column label="动作" width="140">
      <template v-slot="{ row }">
        <span>{{ actionName(row.action) }}</span>
      </template>
    </el-table-column>
    <!-- 列宽一律写死（与「入职邀请」列表同一写法）。这里不能用「弹性列」：
         Element 的 el-table 在只剩一个未给 width 的列时，会把容器剩余宽度**整份**灌给它
         （table-layout.js: flexColumns.length === 1 → realWidth = minWidth + totalFlexWidth），
         结果这一列与后面的 IP 列之间空出一大片，而前面几列挤在一起——列间距不一致，
         多余的宽度应该留在表格右侧，而不是塞进中间某一列。 -->
    <el-table-column label="变更摘要" width="420">
      <template v-slot="{ row }">
        <router-link v-if="row.change_count" :to="detailTo(row)" class="change-cell">
          <div
            v-for="change in visibleChanges(row)"
            :key="`${row.id}-${change.field}`"
            class="change-line"
            :title="changeTitle(change)"
          >
            <span v-if="change.kind !== 'update'" class="change-kind">{{ kindName(change) }}</span>
            <span class="change-label">{{ change.label }}</span>
            <template v-if="change.kind === 'update'">
              <span class="change-value change-value-old">{{ change.from }}</span>
              <span class="change-arrow">→</span>
              <span class="change-value change-value-new">{{ change.to }}</span>
            </template>
            <span v-else class="change-value">{{ changeValue(change) }}</span>
          </div>
          <div v-if="moreChangesCount(row)" class="change-more">…还有 {{ moreChangesCount(row) }} 项变更</div>
        </router-link>
        <span v-else class="info-color">无字段变更</span>
      </template>
    </el-table-column>
    <el-table-column label="IP" prop="ip" width="140"/>
    <el-table-column label="时间" width="150">
      <template v-slot="{ row }">{{ row.add_time | timeToYmdHi }}</template>
    </el-table-column>
    <!-- 单个 2 字带图标按钮（v-list-button small ≈ 85px）+ 单元格左右内边距 20px，
         列宽下限 140（与 OnboardApplyList 的操作列一致）；90 会把按钮右半边裁掉 -->
    <el-table-column label="详情" width="140">
      <template v-slot="{ row }">
        <v-list-button name="详情" icon="view" :to="detailTo(row)" :is-last="true"/>
      </template>
    </el-table-column>

    <template slot="mobile-list">
      <section class="list">
        <template v-if="list.length">
          <div
            v-for="(v, k) in list"
            :key="`operation-log-${k}-${v.id}`"
            class="list-item primary-background-light"
          >
            <div class="list-item-detail">
              <span>操作人</span>
              <span>{{ v.operator_nickname || `#${v.operator_id}` }}</span>
            </div>
            <div class="list-item-detail">
              <span>操作对象</span>
              <span>
                <el-tag :type="targetTypeTag(v)" size="mini">{{ v.target_type_name }}</el-tag>
                {{ targetDisplayName(v) }}
                <span v-if="!v.target_exists" class="target-gone">已失效</span>
              </span>
            </div>
            <div v-if="v.target_desc" class="list-item-detail">
              <span>对象说明</span>
              <span>{{ v.target_desc }}</span>
            </div>
            <div class="list-item-detail">
              <span>动作</span>
              <span>{{ actionName(v.action) }}</span>
            </div>
            <div class="list-item-detail">
              <span>变更摘要</span>
              <router-link v-if="v.change_count" :to="detailTo(v)" class="change-cell">
                <div v-for="change in visibleChanges(v)" :key="`mobile-${v.id}-${change.field}`" class="change-line">
                  <span v-if="change.kind !== 'update'" class="change-kind">{{ kindName(change) }}</span>
                  <span class="change-label">{{ change.label }}</span>
                  <template v-if="change.kind === 'update'">
                    <span class="change-value change-value-old">{{ change.from }}</span>
                    <span class="change-arrow">→</span>
                    <span class="change-value change-value-new">{{ change.to }}</span>
                  </template>
                  <span v-else class="change-value">{{ changeValue(change) }}</span>
                </div>
                <div v-if="moreChangesCount(v)" class="change-more">…还有 {{ moreChangesCount(v) }} 项变更</div>
              </router-link>
              <span v-else class="info-color">无字段变更</span>
            </div>
            <div class="list-item-detail">
              <span>IP</span>
              <span>{{ v.ip }}</span>
            </div>
            <div class="list-item-detail">
              <span>时间</span>
              <span>{{ v.add_time | timeToYmdHi }}</span>
            </div>
          </div>
        </template>
        <v-empty :is-loading="isLoading" :length="list.length"/>
      </section>
    </template>
  </v-table>
</template>

<script>
import RouteConfig from '@/router/config';
import { operationLogIndex } from '@/api/personnel';
import { operationActionGroups, findOperationAction } from '@/enums/operationActions';
import { operationTargetTypeOptions, findOperationTargetType, findOperationChangeKind } from '@/enums/operationLogs';
import { getLegalQueryList } from '@/utils/formats';

export default {
  name: RouteConfig.administratorOperationLogList.name,
  data: () => ({
    RouteConfig,
    count: 0,
    list: [],
    isLoading: false,
    queryList: {
      is_asc: 0,
      administrator_ids: [],
      update_administrator_ids: [],
      action: '',
      target_type: '',
      keyword: '',
      page: 1,
      size: 10,
    },
    actionGroups: operationActionGroups,
    targetTypeOptions: operationTargetTypeOptions,
    // 列表里每行最多展示 2 条变更，其余折叠为「还有 N 项」，详情页看全量
    changeVisibleCount: 2,
  }),
  watch: {
    '$route'(to) {
      this.load(to);
    },
  },
  beforeMount() {
    this.load();
  },
  methods: {
    refresh() {
      RouteConfig.administratorOperationLogList.push(this.queryList);
    },
    handleFilterChange() {
      this.queryList.page = 1;
      this.refresh();
    },
    actionName(action) {
      return findOperationAction(action).name;
    },
    // 操作对象：target_id 一列多指向，展示与跳转都由后端解析出的 target_* 决定
    targetDisplayName(row) {
      return row.target_name || `#${row.target_id}`;
    },
    targetTypeTag(row) {
      return findOperationTargetType(row.target_type).tagType;
    },
    targetLink(row) {
      // 对象已被移除（任职记录物理删除）或账号已注销时不给链接，避免点进去 404
      if (!row.target_exists) return '';
      const linkKey = findOperationTargetType(row.target_type).linkKey;
      const page = linkKey && RouteConfig[linkKey];
      return page && page.to ? page.to(row.target_id) : '';
    },
    detailTo(row) {
      return RouteConfig.administratorOperationLogDetail.to(row.id);
    },
    // 变更行的文案：kind 由后端判定（update 才是新旧对比，其余只有一侧有值）
    kindName(change) {
      return findOperationChangeKind(change.kind).name;
    },
    changeValue(change) {
      // result = 本次操作写入的值（与 create 同属"只有新值"）
      return change.kind === 'create' || change.kind === 'result' ? change.to : (change.from || change.to);
    },
    // 摘要列宽有限，字段归属表放在悬浮提示里：同一次操作可能同时改管理员表与任职薪资表
    changeTitle(change) {
      const column = change.table ? `${change.table}.${change.field}` : change.field;
      return `${change.table_name || ''}｜${column}`;
    },
    // 变更明细已由后端算好（字段中文名、ID 换名称、金额/时间格式化），前端只负责排版
    visibleChanges(row) {
      return (row.changes || []).slice(0, this.changeVisibleCount);
    },
    moreChangesCount(row) {
      return Math.max((row.change_count || 0) - this.changeVisibleCount, 0);
    },
    async load(route = this.$route, next) {
      const { queryList } = this;
      getLegalQueryList(queryList, route.query);

      this.isLoading = true;
      const { isError, data } = await operationLogIndex(queryList);
      this.isLoading = false;
      if (isError) return;

      this.count = data.count;
      this.list = data.list;
      if (next) next();
    },
  },
};
</script>

<style scoped>
.target-cell {
  line-height: 1.6;
}

.target-line {
  display: flex;
  align-items: center;
  /* 原本 4px：类型标签自带边框 + 对象名 + 已失效三段几乎粘在一起，看着挤 */
  gap: 8px;
  overflow: hidden;
}

.target-tag {
  flex-shrink: 0;
}

.target-name {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* 只有真有详情页可跳的对象才上主题蓝；已失效（不给链接）保持正文色，
   免得满屏蓝点却点不动。#409EFF 与 common.css 的 .primary-color 同值 */
.target-name--link {
  color: #409EFF;
}

.target-name--link:hover {
  text-decoration: underline;
}

.target-gone {
  flex-shrink: 0;
  color: #606266;
  font-size: 12px;
}

.target-desc {
  margin-top: 4px;
  color: #606266;
  font-size: 12px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* 变更摘要：一条变更一行，整块可点进详情页看全量 */
.change-cell {
  display: block;
  color: inherit;
}

/* 常态不上色（摘要最多两行、一屏十条，整列蓝字非常扎眼），
   悬停时整块变主题色，作为"这一格可以点进详情页"的反馈 */
.change-cell:hover,
.change-cell:hover .change-label,
.change-cell:hover .change-value,
.change-cell:hover .change-value-new,
.change-cell:hover .change-arrow,
.change-cell:hover .change-more {
  color: #409EFF;
}

.change-line {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
  line-height: 1.7;
}

.change-kind {
  flex-shrink: 0;
  padding: 0 4px;
  border-radius: 2px;
  background: #f0f2f5;
  color: #606266;
  font-size: 12px;
}

.change-label {
  color: #606266;
}

.change-label::after {
  content: '：';
}

/* 摘要列固定 420，一行要装下「标签 + 字段名 + 旧值 → 新值」，单值超 150 就省略号，详情页看全量 */
.change-value {
  max-width: 150px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* 新旧值不只靠颜色区分（旧值删除线、新值加粗），配色与详情页同一套 */
.change-value-new {
  color: #529b2e;
  font-weight: bold;
}

.change-value-old {
  color: #c45656;
  text-decoration: line-through;
}

.change-arrow {
  color: #909399;
}

.change-more {
  color: #606266;
  font-size: 12px;
}
</style>
